import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { recordsAPI, inspectionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn, formatDate, formatDateTime, getStatusColor, getRecordTypeLabel, getInspectionTypeLabel, downloadBlob } from '../lib/utils';
import {
  ArrowLeft,
  Car,
  Truck,
  Bus,
  Package,
  Edit,
  Trash2,
  Plus,
  FileText,
  Download,
  QrCode,
  CheckCircle,
  XCircle,
  Loader2,
  Cylinder,
  Calendar,
  User,
  MapPin,
  ShieldCheck,
  Image,
} from 'lucide-react';
import { toast } from 'sonner';

const recordTypeIcons = {
  car: Car,
  '3w': Car,
  bus: Bus,
  truck: Truck,
  trailer: Truck,
  cng_storage: Package,
};

export default function RecordDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, canEdit, canDelete } = useAuth();
  const [record, setRecord] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [recordRes, inspectionsRes] = await Promise.all([
        recordsAPI.getById(id),
        inspectionsAPI.getAll({ record_id: id }),
      ]);
      setRecord(recordRes.data);
      setInspections(inspectionsRes.data);
    } catch (err) {
      toast.error('Failed to load record');
      navigate('/records');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    setDeleting(true);
    try {
      await recordsAPI.delete(id, deleteReason);
      toast.success('Record deleted');
      navigate('/records');
    } catch (err) {
      toast.error('Failed to delete record');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadCertificate = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      downloadBlob(response.data, `certificate_${inspectionId}.pdf`);
      toast.success('Certificate downloaded');
    } catch (err) {
      toast.error('Failed to download certificate');
    }
  };

  const handleDownloadQR = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      downloadBlob(response.data, `qr_sticker_${inspectionId}.pdf`);
      toast.success('QR sticker PDF downloaded');
    } catch (err) {
      toast.error('Failed to download QR sticker');
    }
  };

  const handleDownloadQRImage = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRImage(inspectionId);
      downloadBlob(response.data, `qr_code_${inspectionId}.png`);
      toast.success('QR code image downloaded');
    } catch (err) {
      toast.error('Failed to download QR image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!record) {
    return null;
  }

  const Icon = recordTypeIcons[record.record_type] || FileText;
  const latestStatus = record.latest_inspection?.status || 'none';
  const identifier = record.registration_number || record.storage_serial_number;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="record-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/records')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold font-mono">
                  {identifier}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline">{getRecordTypeLabel(record.record_type)}</Badge>
                  <Badge 
                    variant="outline"
                    className={cn("capitalize", getStatusColor(latestStatus))}
                  >
                    {latestStatus === 'none' ? 'No Inspection' : latestStatus}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 ml-14 md:ml-0">
          {canEdit && (
            <Button variant="outline" asChild>
              <Link to={`/records/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canEdit && (
            <Button asChild data-testid="new-inspection-btn">
              <Link to={`/inspections/new?record_id=${id}`}>
                <Plus className="mr-2 h-4 w-4" />
                New Inspection
              </Link>
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Record</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will soft-delete the record. It can be recovered by an administrator.
                    Please provide a reason for this action.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Label htmlFor="reason">Reason for deletion</Label>
                  <Input
                    id="reason"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Enter reason..."
                    className="mt-2"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground">
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle/Storage Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Record Type</p>
                    <p className="font-medium">{getRecordTypeLabel(record.record_type)}</p>
                  </div>
                  {record.registration_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">Registration Number</p>
                      <p className="font-mono font-medium">{record.registration_number}</p>
                    </div>
                  )}
                  {record.storage_serial_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">Storage Serial Number</p>
                      <p className="font-mono font-medium">{record.storage_serial_number}</p>
                    </div>
                  )}
                  {record.vin_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">VIN Number</p>
                      <p className="font-mono text-sm">{record.vin_number}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {record.make && (
                    <div>
                      <p className="text-sm text-muted-foreground">Make</p>
                      <p className="font-medium">{record.make}</p>
                    </div>
                  )}
                  {record.model && (
                    <div>
                      <p className="text-sm text-muted-foreground">Model</p>
                      <p className="font-medium">{record.model}</p>
                    </div>
                  )}
                  {record.year && (
                    <div>
                      <p className="text-sm text-muted-foreground">Year</p>
                      <p className="font-medium">{record.year}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">NGV Origin</p>
                    <p className="font-medium capitalize">{record.ngv_origin?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>

              {/* Conversion Kit Info (for Retrofitted) */}
              {record.ngv_origin === 'retrofitted' && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <h4 className="font-medium mb-4">Conversion Kit Details</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {record.conversion_kit_manufacturer && (
                        <div>
                          <p className="text-sm text-muted-foreground">Kit Manufacturer</p>
                          <p className="font-medium">{record.conversion_kit_manufacturer}</p>
                        </div>
                      )}
                      {record.conversion_kit_model && (
                        <div>
                          <p className="text-sm text-muted-foreground">Kit Model</p>
                          <p className="font-medium">{record.conversion_kit_model}</p>
                        </div>
                      )}
                      {record.installer_name && (
                        <div>
                          <p className="text-sm text-muted-foreground">Installer</p>
                          <p className="font-medium">{record.installer_name}</p>
                        </div>
                      )}
                      {record.installer_license && (
                        <div>
                          <p className="text-sm text-muted-foreground">Installer License</p>
                          <p className="font-mono text-sm">{record.installer_license}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Owner Info (Admin only) */}
          {isAdmin && (record.owner_name || record.owner_phone || record.owner_email) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Owner Information
                </CardTitle>
                <CardDescription>Visible to administrators only</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {record.owner_name && (
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{record.owner_name}</p>
                    </div>
                  )}
                  {record.owner_phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{record.owner_phone}</p>
                    </div>
                  )}
                  {record.owner_email && (
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{record.owner_email}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cylinders */}
          {record.cylinders && record.cylinders.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cylinder className="h-5 w-5" />
                  Cylinders ({record.cylinders.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead>Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {record.cylinders.map((cyl, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{cyl.serial_number}</TableCell>
                        <TableCell>{cyl.manufacturer || '-'}</TableCell>
                        <TableCell>{cyl.capacity ? `${cyl.capacity}L` : '-'}</TableCell>
                        <TableCell>{cyl.expiry_date || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Inspection History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Inspection History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {inspections.length === 0 ? (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No inspections yet</p>
                  {canEdit && (
                    <Button asChild className="mt-4">
                      <Link to={`/inspections/new?record_id=${id}`}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create Inspection
                      </Link>
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Valid Until</TableHead>
                      <TableHead>Verification</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inspections.map((insp) => (
                      <TableRow key={insp.id} className="table-row-hover">
                        <TableCell>{formatDate(insp.created_at)}</TableCell>
                        <TableCell>{getInspectionTypeLabel(insp.inspection_type)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={cn("capitalize", getStatusColor(insp.status))}
                          >
                            {insp.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(insp.valid_to)}</TableCell>
                        <TableCell className="font-mono text-xs">{insp.verification_number}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadCertificate(insp.id)}
                              title="Download Certificate"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadQR(insp.id)}
                              title="Download QR as PDF"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadQRImage(insp.id)}
                              title="Download QR as Image"
                            >
                              <Image className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Latest Inspection Summary */}
          {record.latest_inspection && (
            <Card className={cn(
              "border-l-4",
              record.latest_inspection.status === 'valid' ? 'border-l-emerald-500' :
              record.latest_inspection.status === 'expired' ? 'border-l-red-500' :
              'border-l-amber-500'
            )}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Latest Inspection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge 
                    variant="outline"
                    className={cn("capitalize", getStatusColor(record.latest_inspection.status))}
                  >
                    {record.latest_inspection.status === 'valid' && <CheckCircle className="mr-1 h-3 w-3" />}
                    {record.latest_inspection.status === 'expired' && <XCircle className="mr-1 h-3 w-3" />}
                    {record.latest_inspection.status}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Valid From</span>
                  <span className="text-sm font-medium">{formatDate(record.latest_inspection.valid_from)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Valid Until</span>
                  <span className="text-sm font-medium">{formatDate(record.latest_inspection.valid_to)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Next Inspection</span>
                  <span className="text-sm font-medium">{formatDate(record.latest_inspection.next_inspection_date)}</span>
                </div>

                <Separator />

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Verification Number</p>
                  <p className="font-mono text-sm">{record.latest_inspection.verification_number}</p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDownloadCertificate(record.latest_inspection.id)}
                  >
                    <Download className="mr-1 h-3 w-3" />
                    Certificate
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDownloadQR(record.latest_inspection.id)}
                    title="Download QR as PDF"
                  >
                    <FileText className="mr-1 h-3 w-3" />
                    QR PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDownloadQRImage(record.latest_inspection.id)}
                    title="Download QR as Image"
                  >
                    <Image className="mr-1 h-3 w-3" />
                    QR Image
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDateTime(record.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Record ID</span>
                <span className="font-mono text-xs">{record.id.slice(0, 8)}...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
